/* =========================================================================
   WISHLIST + CART (Horizon 2.1.4 compatible)
   - localStorage wishlist
   - header wishlist counter sync (desktop + mobile sections)
   - add-to-cart from wishlist:
       * posts to /cart/add with ?sections=… (same pattern as product-form)
       * swaps returned sections HTML into the DOM
       * if no sections returned / can’t find nodes, fetches via /?sections=…
       * opens the drawer and keeps the bubble in sync
   ======================================================================= */

/* ----------------------- Wishlist (local only) ------------------------ */
(() => {
  const KEY = 'wishlist_product_handles';

  const getList = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]') || []; }
    catch { return []; }
  };
  const saveList = (arr) => {
    localStorage.setItem(KEY, JSON.stringify(arr));
    window.dispatchEvent(new Event('wishlist:updated'));
  };
  const inList = (h) => getList().includes(h);
  const add = (h) => { const L = getList(); if (!L.includes(h)) { L.push(h); saveList(L); } };
  const remove = (h) => saveList(getList().filter(x => x !== h));

  const syncHearts = (root = document) => {
    root.querySelectorAll('[data-wl-toggle][data-product-handle]').forEach(btn => {
      const handle = btn.getAttribute('data-product-handle');
      btn.setAttribute('aria-pressed', inList(handle) ? 'true' : 'false');
      btn.type = 'button';
    });
  };

  const refreshWLCount = () => {
    const n = getList().length;
    document.querySelectorAll('[data-wl-count]').forEach(el => { el.textContent = n; });
  };

  const toggleClick = (e) => {
    const btn = e.target.closest('[data-wl-toggle][data-product-handle]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.getAttribute('data-product-handle');
    const pressed = btn.getAttribute('aria-pressed') === 'true';
    (pressed ? remove : add)(handle);
    btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
  };

  const initWL = () => {
    syncHearts(document);
    refreshWLCount();

    document.addEventListener('click', toggleClick);
    const onSecLoad = (ev) => { syncHearts(ev.target); refreshWLCount(); };
    document.addEventListener('shopify:section:load', onSecLoad);
    document.addEventListener('theme:section:load', onSecLoad);

    window.addEventListener('wishlist:updated', refreshWLCount);
    window.addEventListener('storage', (ev) => { if (ev.key === KEY) refreshWLCount(); });
    window.addEventListener('pageshow', refreshWLCount);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshWLCount(); });

    // MutationObserver to catch late-mounted headers on mobile
    const mo = new MutationObserver((muts) => {
      let changed = false;
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (!(n instanceof Element)) continue;
          if (n.matches?.('[data-wl-count],[data-wl-toggle],header-actions,header,.header')) changed = true;
          else if (n.querySelector?.('[data-wl-count],[data-wl-toggle]')) changed = true;
        }
      }
      if (changed) { syncHearts(document); refreshWLCount(); }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWL, { once: true });
  } else {
    initWL();
  }
})();

/* ----------------- Cart bubble + drawer helpers ---------------------- */
(() => {
  /** Update the cart bubble and unhide it if it was hidden (cart empty → first add). */
  const setCartCount = (count) => {
    document.querySelectorAll('.cart-bubble__text').forEach(el => {
      el.textContent = String(count);
      el.classList.remove('hidden'); // Horizon hides when 0
    });
    document.querySelectorAll('.header-actions__cart-icon .cart-bubble').forEach(b => {
      b.classList.remove('visually-hidden'); // Horizon hides when 0
      if (count > 0) b.classList.add('cart-bubble--animating');
    });
    document.querySelectorAll('.header-actions__cart-icon').forEach(icon => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
    // Broadcast for theme listeners (cart-icon.js listens to a cartUpdate event; we also emit generic ones)
    window.dispatchEvent(new CustomEvent('cart:count:updated', { detail: { count } }));
    window.dispatchEvent(new CustomEvent('cart:updated',      { detail: { item_count: count } }));
  };

  const fetchCartCount = async () => {
    try {
      const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
      const cart = await res.json();
      setCartCount(cart?.item_count ?? 0);
    } catch {}
  };

  /** Find all cart drawer section IDs present in the DOM (works whether drawer is open or closed). */
  const collectCartSectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(el => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.add(id);
    });
    return Array.from(ids);
  };

  /** Swap returned sections into their section wrappers. */
  const swapSections = (sectionsMap = {}) => {
    let swapped = false;

    Object.entries(sectionsMap).forEach(([id, html]) => {
      // Normal OS 2.0 wrapper
      let host = document.getElementById(`shopify-section-${id}`);
      // Fallback: find any wrapper that has the matching data-section-id
      if (!host) host = document.querySelector(`[data-section-id="${CSS.escape(id)}"]`)?.closest('section,div,[id^="shopify-section-"]');

      if (host && typeof html === 'string') {
        const tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        const fresh = tmp.firstElementChild || tmp;
        host.replaceWith(fresh);
        swapped = true;
      }
    });

    // Nudge theme to re-bind behaviors
    if (swapped) {
      document.dispatchEvent(new Event('shopify:section:load'));
      document.dispatchEvent(new Event('theme:section:load'));
    }
    return swapped;
  };

  /** If /cart/add didn’t return sections or swap failed, pull them via the Sections API. */
  const refreshSectionsByAPI = async () => {
    const ids = collectCartSectionIds();
    if (!ids.length) return false;

    try {
      const url = `/?sections=${encodeURIComponent(ids.join(','))}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      const json = await res.json();
      return swapSections(json);
    } catch {
      return false;
    }
  };

  /** Open the drawer via the theme element if available. */
  const openDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    try { drawer?.open?.(); } catch {}
  };

  /** Soft-close drawer (we don’t force-close here, we only open after add). */
  const closeDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    try { drawer?.close?.(); } catch {}
  };

  /* ----------------- Wishlist → Add to Cart wiring ------------------- */

  /** POST /cart/add with sections=… so Shopify returns fresh HTML like product-form.js does. */
  const postAddToCart = async (formData) => {
    const sectionIds = collectCartSectionIds();
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const res = await fetch('/cart/add', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'text/html' }, // Shopify returns JSON (with sections) to 'text/html'
      credentials: 'same-origin'
    });

    // If add fails (422 etc.), let it throw so we can fall back
    if (!res.ok) throw new Error(`Add failed: ${res.status}`);

    // Shopify returns JSON with keys: id, quantity, sections (map), etc.
    const data = await res.json().catch(() => null);
    return data || {};
  };

  /** Try to mimic Horizon’s native flow so cart-icon + drawer react naturally. */
  const emitThemeCartEvents = (qtyAdded = 1) => {
    // Most themes listen to one or more of these:
    try { window.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { quantity: qtyAdded } })); } catch {}
    try { window.dispatchEvent(new CustomEvent('cart:add',       { detail: { quantity: qtyAdded } })); } catch {}
  };

  /** Unified handler after a successful /cart/add */
  const afterAddSuccess = async (data, qty = 1) => {
    // 1) Swap sections if present
    let swapped = false;
    if (data?.sections) swapped = swapSections(data.sections);
    if (!swapped) swapped = await refreshSectionsByAPI();

    // 2) Ensure bubble count correct (also makes the bubble visible when going 0 → 1)
    await fetchCartCount();

    // 3) Nudge theme listeners (harmless if unused)
    emitThemeCartEvents(qty);

    // 4) Open drawer so the user sees the added line immediately
    openDrawer();
  };

  /** Intercept wishlist page form submits */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    // Only run on wishlist page/section (so collection/PDP behave natively)
    const onWishlist = /wishlist/i.test(location.pathname) ||
                       document.querySelector('[data-wishlist-page],[data-wishlist-section]');
    if (!onWishlist) return;

    e.preventDefault();
    const fd  = new FormData(form);
    const qty = parseInt(fd.get('quantity') || '1', 10);

    try {
      const data = await postAddToCart(fd);
      await afterAddSuccess(data, qty);
    } catch (err) {
      // Fallback to normal submit if AJAX fails
      form.submit();
    }
  });

  /** Intercept simple buttons with data-variant-id inside wishlist */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-wishlist-add-to-cart][data-variant-id]');
    if (!btn) return;

    const onWishlist = /wishlist/i.test(location.pathname) ||
                       document.querySelector('[data-wishlist-page],[data-wishlist-section]');
    if (!onWishlist) return;

    e.preventDefault();
    const fd = new FormData();
    fd.set('id', btn.getAttribute('data-variant-id') || '');
    fd.set('quantity', btn.getAttribute('data-quantity') || '1');

    const qty = parseInt(fd.get('quantity') || '1', 10);

    try {
      const data = await postAddToCart(fd);
      await afterAddSuccess(data, qty);
    } catch {}
  });

  /* ----------- Keep things in sync across loads/sections -------------- */
  // Initial bubble sync
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCartCount, { once: true });
  } else {
    fetchCartCount();
  }
  document.addEventListener('shopify:section:load', fetchCartCount);
  window.addEventListener('pageshow', fetchCartCount);

  // If the drawer trigger is clicked, refresh shortly after it opens
  document.addEventListener('click', (e) => {
    const trg = e.target.closest('.action__cart, [data-cart-trigger], a[href^="/cart"]');
    if (trg) setTimeout(fetchCartCount, 150);
  });

})();
